import React from 'react';
import { Box, TextField, Chip, Autocomplete, Checkbox } from '@mui/material';
import type { VolleyballSettings, VolleyballTieBreaker } from '@/types';
import type { SportSettingsFormProps } from './types';
import { SettingsSection } from './SettingsSection';
import { SettingsField, SettingsFieldsGrid } from './SettingsFieldsGrid';

const TIEBREAKER_OPTIONS: { value: VolleyballTieBreaker; label: string }[] = [
  { value: 'sets_ratio',   label: 'Соотношение сетов' },
  { value: 'points_ratio', label: 'Соотношение очков' },
  { value: 'head_to_head', label: 'Личные встречи' },
];

export const VolleyballSettingsForm: React.FC<SportSettingsFormProps> = ({ value, onChange, disabled }) => {
  const s = value as VolleyballSettings;
  const upd = (patch: Partial<VolleyballSettings>) => onChange({ ...s, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <SettingsSection title="Структура матча">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Сетов в матче" type="number" size="small" fullWidth
              value={s.sets ?? 5}
              onChange={(e) => upd({ sets: parseInt(e.target.value) || 5 })}
              slotProps={{ htmlInput: { min: 1, max: 5, step: 2 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Очков для победы в сете" type="number" size="small" fullWidth
              value={s.pointsToWinSet ?? 25}
              onChange={(e) => upd({ pointsToWinSet: parseInt(e.target.value) || 25 })}
              slotProps={{ htmlInput: { min: 10 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Очков в решающем сете" type="number" size="small" fullWidth
              value={s.finalSetPoints ?? 15}
              onChange={(e) => upd({ finalSetPoints: parseInt(e.target.value) || 15 })}
              slotProps={{ htmlInput: { min: 10 } }}
              disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Очки в турнирной таблице">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Победа 3:0 / 3:1" type="number" size="small" fullWidth
              value={s.pointsWin ?? 3}
              onChange={(e) => upd({ pointsWin: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Победа 3:2" type="number" size="small" fullWidth
              value={s.pointsWinAway ?? 2}
              onChange={(e) => upd({ pointsWinAway: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Поражение 2:3" type="number" size="small" fullWidth
              value={s.pointsLossAway ?? 1}
              onChange={(e) => upd({ pointsLossAway: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Поражение 0:3 / 1:3" type="number" size="small" fullWidth
              value={s.pointsLoss ?? 0}
              onChange={(e) => upd({ pointsLoss: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Тай-брейки">
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={TIEBREAKER_OPTIONS.map((o) => o.value)}
          value={s.tieBreakers ?? []}
          onChange={(_, v) => upd({ tieBreakers: v as VolleyballTieBreaker[] })}
          getOptionLabel={(o) => TIEBREAKER_OPTIONS.find((x) => x.value === o)?.label ?? o}
          disabled={disabled}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
              {TIEBREAKER_OPTIONS.find((x) => x.value === option)?.label ?? option}
            </li>
          )}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip label={`${idx + 1}. ${TIEBREAKER_OPTIONS.find((x) => x.value === opt)?.label ?? opt}`}
                size="small" {...getTagProps({ index: idx })} key={opt} />
            ))
          }
          renderInput={(params) => <TextField {...params} size="small" fullWidth placeholder="Добавить критерий..." />}
        />
      </SettingsSection>

    </Box>
  );
};
